export type TruncateTextOptions =
  | {
      words: number;
      ellipsis?: string;
    }
  | {
      sentences: number;
      ellipsis?: string;
    };

export default class IntlTextTruncator {
  private options: TruncateTextOptions;

  private sentenceSegmenter: Intl.Segmenter;

  private wordSegmenter: Intl.Segmenter;

  constructor(locale: string | undefined, options: TruncateTextOptions) {
    this.options = options;
    this.sentenceSegmenter = new Intl.Segmenter(locale, {
      granularity: 'sentence',
    });
    this.wordSegmenter = new Intl.Segmenter(locale, { granularity: 'word' });
  }

  countWords(text: string) {
    const segments = this.wordSegmenter.segment(text);
    return [...segments].filter((segment) => segment.isWordLike).length;
  }

  format(text: string): string {
    let truncatedText = '';
    const ellipsis = 'ellipsis' in this.options ? this.options.ellipsis : '';

    if ('sentences' in this.options) {
      truncatedText = [...this.sentenceSegmenter.segment(text)]
        .slice(0, this.options.sentences)
        .map((segment) => segment.segment)
        .join('');
    } else if ('words' in this.options) {
      let wordsLeft = this.options.words;
      const sentenceSegments = this.sentenceSegmenter.segment(text);
      // eslint-disable-next-line no-restricted-syntax
      for (const sentenceSegment of sentenceSegments) {
        if (wordsLeft === 0) {
          break;
        }
        const wordSegments = [
          ...this.wordSegmenter.segment(sentenceSegment.segment),
        ];
        const wordCount = wordSegments.filter(
          (segment) => segment.isWordLike,
        ).length;

        if (wordsLeft >= wordCount) {
          truncatedText += sentenceSegment.segment;
          wordsLeft -= wordCount;
          // eslint-disable-next-line no-continue
          continue;
        }
        // eslint-disable-next-line no-restricted-syntax
        for (const wordSegment of wordSegments) {
          if (wordsLeft === 0) {
            if (wordSegment.isWordLike) {
              break;
            }
            const rest = wordSegments.slice(
              wordSegments.indexOf(wordSegment) + 1,
            );
            if (rest.some((segment) => segment.isWordLike)) {
              break;
            }
          }
          if (wordSegment.isWordLike) {
            wordsLeft -= 1;
          }
          truncatedText += wordSegment.segment;
        }
        break;
      }
    } else {
      throw new Error('Invalid options');
    }

    return truncatedText.length < text.length
      ? `${truncatedText}${ellipsis}`
      : truncatedText;
  }
}
